// The ready-made agents on the Templates page. Vocabulary: a SIGNAL is an outside change from the Signals
// feed; an EVENT is something inside Salesgear; an AGENT is a multi-step job that starts one of the ways in STARTS.

export const STARTS = {
  signal: "Starts from your Signals",
  event: "Starts when it happens in Salesgear",
  daily: "Runs on a schedule · Daily",
  weekly: "Runs on a schedule · Weekly",
  hourly: "Runs on a schedule · Hourly",
  manual: "Runs when you run it",
  copilot: "Runs from Copilot"
};

/** The "Starts" filter groups the three schedule cadences into one choice. */
export const startGroup = starts => (["daily", "weekly", "hourly"].includes(starts) ? "schedule" : starts);
export const START_FILTERS = [
  { value: "signal", label: "From your Signals" },
  { value: "event", label: "When it happens in Salesgear" },
  { value: "schedule", label: "On a schedule" },
  { value: "manual", label: "When you run it" }
];

// Templates are grouped by the team that runs them.
export const TEAMS = ["Sales", "Marketing", "Revenue operations", "Account management", "Competitive"];
export const WORKS_WITH = ["Email", "LinkedIn", "Sequences", "Lists", "Slack", "CRM"];

/**
 * Colour family of a signal category (from the Signals catalog, or a template's own):
 * people violet, account green, competitor coral, event amber, own activity pink.
 */
export const familyOf = category => {
  const c = String(category || "").toLowerCase();
  if (c.includes("people") || c.includes("person")) return "people";
  if (c.includes("company") || c.includes("account")) return "account";
  if (c.includes("competitor")) return "competitor";
  if (c.includes("event")) return "eventfam";
  return "own";
};

// trigger: the first chip, written "When …". kind "signal" is coloured by family; kind "event" is neutral.
export const TEMPLATES = [
  {
    key: "signal", icon: "wifi_tethering", name: "Signal agent", starts: "signal", team: "Sales",
    does: "Acts on a signal you track, like a job change or hot opens.",
    trigger: { kind: "signal", family: "people", label: "When a signal you track fires" },
    steps: [{ icon: "check_circle", label: "Act on it" }, { icon: "notifications", label: "Notify you" }],
    worksWith: ["Email", "Sequences", "Lists", "Slack"], to: "/agents/new/signal"
  },
  {
    key: "champion", icon: "military_tech", name: "Champion follow-up", starts: "signal", team: "Account management",
    does: "When a past champion lands somewhere new, finds their new email and starts your sequence once you approve.",
    trigger: { kind: "signal", family: "people", label: "When a champion changes jobs" },
    steps: [{ icon: "email", label: "Find email" }, { icon: "check_circle", label: "You approve" }, { icon: "send", label: "Enroll in sequence" }],
    worksWith: ["Email", "Sequences"], to: "/agents/new/signal?type=champion_move&template=enroll_sequence"
  },
  {
    key: "account-brief", icon: "apartment", name: "Target account brief", starts: "signal", team: "Sales", soon: true,
    does: "A brief and a person to reach when an account changes.",
    trigger: { kind: "signal", family: "account", label: "When a target account changes" },
    steps: [{ icon: "psychology", label: "Research" }, { icon: "person_add", label: "Who to reach" }, { icon: "assignment", label: "Brief" }],
    worksWith: ["Lists"]
  },
  {
    key: "competitor", icon: "compare_arrows", name: "Competitor play", starts: "signal", team: "Competitive", soon: true,
    does: "An angle and a draft while they compare tools.",
    trigger: { kind: "signal", family: "competitor", label: "When your ICP engages a competitor" },
    steps: [{ icon: "psychology", label: "Research" }, { icon: "lightbulb", label: "Suggest angle" }, { icon: "create", label: "Draft email" }],
    worksWith: ["Email"]
  },
  {
    key: "event-prospecting", icon: "event", name: "Event prospecting", starts: "signal", team: "Marketing", soon: true,
    does: "Meet ICP attendees before the event.",
    trigger: { kind: "signal", family: "eventfam", label: "When ICP people sign up for an event" },
    steps: [{ icon: "email", label: "Find email" }, { icon: "playlist_add", label: "Add to list" }, { icon: "create", label: "Draft email" }],
    worksWith: ["Email", "Lists"]
  },
  {
    key: "icp", icon: "explore", name: "Daily ICP Prospector", starts: "daily", team: "Sales",
    does: "Finds people who fit your ICP and adds them to a sequence.",
    steps: [{ icon: "person_add", label: "Find people" }, { icon: "email", label: "Find email" }, { icon: "send", label: "Add to sequence" }],
    worksWith: ["Email", "Sequences"], to: "/agents/new/icp"
  },
  {
    key: "list", icon: "list", name: "Prospect from a list", starts: "manual", team: "Sales",
    does: "Enriches a list or saved search and adds it to a sequence.",
    steps: [{ icon: "list", label: "Your list" }, { icon: "email", label: "Find email" }, { icon: "send", label: "Add to sequence" }],
    worksWith: ["Email", "Sequences", "Lists"], to: "/agents/new/list"
  },
  {
    key: "companies", icon: "domain", name: "Companies to people", starts: "manual", team: "Sales",
    does: "Finds companies from a saved search, then the right people there.",
    steps: [{ icon: "domain", label: "Find companies" }, { icon: "person_add", label: "People" }, { icon: "email", label: "Find email" }, { icon: "send", label: "Add to sequence" }],
    worksWith: ["Email", "Sequences"], to: "/agents/new/companies"
  },
  {
    key: "lookalike", icon: "file_copy", name: "Lookalike companies", starts: "manual", team: "Marketing",
    does: "Finds companies like your best accounts, and their people.",
    steps: [{ icon: "file_copy", label: "Lookalikes" }, { icon: "person_add", label: "People" }, { icon: "send", label: "Add to sequence" }],
    worksWith: ["Email", "Sequences"], to: "/agents/new/lookalike"
  },
  {
    key: "abm", icon: "people", name: "Target accounts (ABM)", starts: "manual", team: "Marketing",
    does: "Finds the buying committee at every account on a list.",
    steps: [{ icon: "list", label: "Accounts" }, { icon: "people", label: "Buying committee" }, { icon: "email", label: "Find email" }, { icon: "send", label: "Add to sequence" }],
    worksWith: ["Email", "Sequences", "Lists"], to: "/agents/new/abm"
  },
  {
    key: "linkedin", icon: "download", name: "Import from LinkedIn", starts: "manual", team: "Marketing",
    does: "Imports a search, a post's engagers or an event's attendees.",
    steps: [{ icon: "download", label: "Import" }, { icon: "email", label: "Find email" }, { icon: "send", label: "Add to sequence" }],
    worksWith: ["LinkedIn", "Email", "Sequences"], to: "/agents/new/linkedin"
  },
  {
    key: "reply", icon: "forum", name: "Reply router", starts: "event", team: "Sales",
    does: "Sorts every reply and acts on it.",
    trigger: { kind: "event", label: "When someone replies" },
    steps: [{ icon: "forum", label: "Classify" }, { icon: "check_circle", label: "Act" }],
    worksWith: ["Email", "Sequences"], to: "/agents/new/reply"
  },
  {
    key: "event", icon: "flash_on", name: "Event agent", starts: "event", team: "Revenue operations",
    does: "Acts the moment something happens to a contact.",
    trigger: { kind: "event", label: "When it happens in Salesgear" },
    steps: [{ icon: "check_circle", label: "Act on it" }, { icon: "notifications", label: "Notify you" }],
    worksWith: ["Email", "LinkedIn", "Sequences"], to: "/agents/new/event"
  },
  {
    key: "lost-deal", icon: "history", name: "Lost-deal nurture", starts: "event", team: "Account management", soon: true,
    does: "Wakes lost deals when the account changes.",
    trigger: { kind: "event", label: "When a deal is lost in your CRM" },
    steps: [{ icon: "history", label: "Lost deal" }, { icon: "send", label: "Re-engage" }],
    worksWith: ["CRM", "Sequences"]
  },
  {
    key: "deliverability", icon: "security", name: "Deliverability watchdog", starts: "event", team: "Revenue operations", soon: true,
    does: "Pauses an inbox when bounces spike.",
    trigger: { kind: "event", label: "When bounces spike" },
    steps: [{ icon: "pause", label: "Pause inbox" }, { icon: "notifications", label: "Alert with fix" }],
    worksWith: ["Email"]
  },
  {
    key: "dnc-guard", icon: "report", name: "Do-not-contact guard", starts: "event", team: "Revenue operations", soon: true,
    does: "Never re-enrolls someone marked do not call.",
    trigger: { kind: "event", label: "When a call is marked do not call" },
    steps: [{ icon: "call", label: "Outcome" }, { icon: "block", label: "Never enroll" }],
    worksWith: ["Sequences"]
  },
  {
    key: "activity-report", icon: "bar_chart", name: "Daily activity report", starts: "daily", team: "Revenue operations",
    does: "Yesterday's email, LinkedIn and call numbers, posted to Slack.",
    steps: [{ icon: "bar_chart", label: "Report" }, { icon: "send", label: "Post to Slack" }],
    worksWith: ["Slack"], to: "/agents/new/activity-report"
  },
  {
    key: "competitor-radar", icon: "track_changes", name: "Competitor radar", starts: "weekly", team: "Competitive",
    does: "Every Monday: the teams each competitor is building, what they shipped, where they're headed, and what it means for you.",
    steps: [{ icon: "search", label: "Read competitor moves" }, { icon: "lightbulb", label: "What it means" }, { icon: "send", label: "Post to Slack or email" }],
    worksWith: ["Slack", "Email"],
    // Opens the builder with the server's template composed on a Monday morning schedule.
    compose: { key: "competitor-radar", payload: { frequency: "WEEKLY", days: ["MONDAY"], time: "09:00" } }
  },
  {
    key: "account-scorer", icon: "whatshot", name: "Account scorer", starts: "weekly", team: "Revenue operations",
    does: "Every Monday: scores your account list from its recent signals, keeps the hottest and emails you why.",
    steps: [{ icon: "speed", label: "Score accounts" }, { icon: "filter_alt", label: "Keep 60+" }, { icon: "playlist_add", label: "Save top 20 to a list" }, { icon: "send", label: "Email me" }],
    worksWith: ["Lists", "Email"],
    compose: { key: "account-scorer", payload: { frequency: "WEEKLY", days: ["MONDAY"], time: "09:00" } }
  }
];

/** Filter + sort the templates. f = { team, q, starts:[], works:[], avail:[], sort }. */
/** Templates that need Signals on for the team: signal-started ones, and the two that read scores and competitor moves. */
export const usesSignals = t => t.starts === "signal" || t.key === "account-scorer" || t.key === "competitor-radar";

export const filterTemplates = (list, f) => {
  const q = (f.q || "").trim().toLowerCase();
  const out = list.filter(t =>
    (!f.team || t.team === f.team) &&
    (!q || [t.name, t.does, t.team, STARTS[t.starts], t.trigger && t.trigger.label, ...t.steps.map(s => s.label), ...t.worksWith]
      .join(" ").toLowerCase().includes(q)) &&
    (!(f.starts || []).length || f.starts.includes(startGroup(t.starts))) &&
    (!(f.works || []).length || t.worksWith.some(w => f.works.includes(w))) &&
    (!(f.avail || []).length || f.avail.includes(t.soon ? "soon" : "available"))
  );
  if (f.sort === "az") return out.slice().sort((a, b) => a.name.localeCompare(b.name));
  // Recommended: ready ones first, keeping the order above.
  return out.slice().sort((a, b) => Number(!!a.soon) - Number(!!b.soon));
};
